import { useEffect, useState } from 'react'
import { openStoredPdf } from '@/lib/openPdf'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { TFunction } from 'i18next'
import {
  DocumentTextIcon,
  InboxStackIcon,
  TruckIcon,
  ChevronRightIcon,
  MegaphoneIcon,
  SparklesIcon,
  MapPinIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useSession } from '@/hooks/useSession'
import { useInvoices } from '@/hooks/useInvoices'
import { useParcels } from '@/hooks/useParcels'
import { useVehicles } from '@/hooks/useVehicles'
import { useAnnouncements } from '@/hooks/useAnnouncements'
import { useAuth } from '@/hooks/useAuth'
import { useAccount } from '@/hooks/useAccount'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useRewardsBalance } from '@/hooks/useRewards'
import { isNativePlatform } from '@/lib/native'
import type { Announcement, Contract } from '@/types'

function getGreeting(t: TFunction, name: string): string {
  const hour = new Date().getHours()
  if (hour < 12) return t('greeting_morning', { name })
  if (hour < 17) return t('greeting_afternoon', { name })
  return t('greeting_evening', { name })
}

export default function HomePage() {
  const { t } = useTranslation('home')
  const { t: tc } = useTranslation('common')
  const { t: tMyData } = useTranslation('my_data')
  const { lineProfile } = useAuth()
  const { account } = useAccount()
  const { formatDate } = useDateFormatter()
  const { loyaltyPoints, winPickup } = useFeatureFlags()
  const { data: customer, isLoading: sessionLoading } = useSession()

  const { data: invoicesData } = useInvoices()
  const { data: pendingParcels } = useParcels('pending')
  const { data: vehicles } = useVehicles()
  const { data: announcements } = useAnnouncements()
  const { data: rewardsBalance } = useRewardsBalance(loyaltyPoints)

  // A lease can carry a pdf_key whose object no longer exists: blobs written
  // before the S3 cutover lived only in the Tigris bucket, deleted 2026-08-18.
  // A tenant cannot rebuild one (no template access, and letting a tenant write
  // their own lease document would be worse than a broken link), so the button
  // is replaced with a notice telling them what to ask for.
  //
  // The probe runs here rather than in the click handler for two reasons: it
  // keeps window.open synchronous, which LINE's in-app browser requires, and a
  // plain GET with redirect:'manual' sends no custom header — a header would
  // force a CORS preflight, and a preflighted request cannot follow the
  // cross-origin redirect this endpoint answers with.
  //
  // Fail-open by construction: ONLY an explicit 404 hides the button, so any
  // other outcome leaves today's behaviour untouched.
  const [leasePdfMissing, setLeasePdfMissing] = useState(false)
  const [leasePdfError, setLeasePdfError] = useState<string | null>(null)
  const leasePdfKey = customer?.active_lease?.pdf_key

  useEffect(() => {
    if (!leasePdfKey) return
    let cancelled = false
    const baseUrl = import.meta.env.VITE_API_URL || ''
    fetch(`${baseUrl}/api/images/${leasePdfKey}`, { redirect: 'manual' })
      .then((res) => {
        if (!cancelled && res.type !== 'opaqueredirect' && res.status === 404) setLeasePdfMissing(true)
      })
      .catch(() => {
        /* network hiccup — leave the button alone rather than blame the blob */
      })
    return () => {
      cancelled = true
    }
  }, [leasePdfKey])

  // Owner decision 2026-08-20: offer the document whenever the lease carries a
  // pdf_key, signed or not. It used to require signed_at, on the reasoning that
  // an unsigned draft should never be presented as the agreement — but leases
  // signed on paper and uploaded as a scan never get signed_at set, so their
  // tenants could never see their own contract at all. Availability of the file
  // now decides; the e-sign state does not.
  const leasePdfAvailable = !!leasePdfKey && !leasePdfMissing

  const openLeasePdf = () => {
    if (!leasePdfAvailable) return
    setLeasePdfError(null)
    openStoredPdf(leasePdfKey!, 'lease.pdf').catch(() => {
      setLeasePdfError(tc('errors.something_went_wrong'))
    })
  }

  // EP-156 T156-005 (spec §7 S14, §8 A8-A10) — add-friend card. Shown only
  // inside LINE (never the Capacitor shell, which has no LIFF SDK and no
  // friendship concept), only when liff.getFriendship() resolves to "not a
  // friend yet", and only when the account has a configured LINE OA. null
  // means "not yet known" (still resolving, or the SDK call failed) and the
  // card stays hidden either way — never guess a tenant into a friend-link
  // nag they may already be past.
  const [friendFlag, setFriendFlag] = useState<boolean | null>(null)

  useEffect(() => {
    if (isNativePlatform()) return
    let cancelled = false
    const checkFriendship = async () => {
      try {
        const liff = (await import('@line/liff')).default
        const result = await liff.getFriendship()
        if (!cancelled) setFriendFlag(result.friendFlag)
      } catch {
        // Outside LINE, SDK not ready, or the call itself failed — leave the
        // card hidden rather than show it on a guess.
      }
    }
    checkFriendship()
    return () => {
      cancelled = true
    }
  }, [])

  const lineOaBasicId = account?.line_oa_basic_id
  const showAddFriendCard = !isNativePlatform() && friendFlag === false && !!lineOaBasicId

  const unpaidCount = invoicesData?.data.filter((inv) => inv.status !== 'paid').length ?? 0
  const pendingParcelCount = pendingParcels?.length ?? 0
  const vehicleCount = vehicles?.length ?? 0

  // EP-99 dual-gate: show the WIN Pickup entry only when the Unleash flag AND
  // the account's win_pickup_enabled setting are both on (mirrors web Sidebar).
  const showWinPickup = winPickup && (account?.win_pickup_enabled ?? false)

  // EP-135 Tier 1a — a person holding ANY active OwnerContract is a
  // governance participant. The card surfaces the meeting link to them
  // regardless of whether they ALSO rent a unit (a dual-role person sees
  // BOTH the lease card AND the meeting card). The card link goes to the
  // meeting list (the same page Tier 1 ships at /meetings for operators).
  // Tier 3 will deepen this to a meeting-detail view + ballot entry.
  const hasGovernanceRole =
    customer?.contracts.some((c) => c.type === 'OwnerContract') ?? false
  const governanceContracts: Contract[] =
    customer?.contracts.filter((c) => c.type === 'OwnerContract') ?? []

  if (sessionLoading) {
    return <LoadingSpinner />
  }

  const displayName = customer
    ? `${customer.first_name} ${customer.last_name}`
    : lineProfile?.displayName ?? ''

  const theme = account?.liff_theme

  return (
    <div className="space-y-4 pb-4">
      {/* Teal hero header */}
      <div
        className="bg-primary rounded-b-3xl px-5 pt-12 pb-6 min-h-[160px] flex flex-col justify-end relative"
        style={{
          backgroundColor: theme?.home_hero_color || undefined,
          backgroundImage: theme?.home_hero_image
            ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${theme.home_hero_image})`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* LINE profile avatar */}
        {lineProfile?.pictureUrl && (
          <img
            src={lineProfile.pictureUrl}
            alt={lineProfile.displayName}
            className="absolute top-12 right-5 w-10 h-10 rounded-full border-2 border-white/30 object-cover"
          />
        )}

        {/* Account logo fallback when no picture */}
        {!lineProfile?.pictureUrl && (
          <div className="absolute top-12 right-5 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">
            {account?.name?.[0] ?? 'F'}
          </div>
        )}

        {/* Greeting */}
        {displayName && (
          <p className="text-white text-lg font-bold leading-snug">
            {getGreeting(t, displayName)}
          </p>
        )}

        {/* Account name */}
        {account?.name && (
          <p className="text-white/70 text-sm mt-0.5">{account.name}</p>
        )}
      </div>

      <div className="px-4 space-y-4">
        {/* LINE add-friend card — only inside LINE, only when not yet a
            friend of the account's OA, only when the account has one. */}
        {showAddFriendCard && (
          <div className="flat-card p-4">
            <div className="flex items-center gap-3">
              <div className="icon-container">
                <ChatBubbleLeftRightIcon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-base-content">
                  {t('line_add_friend_title')}
                </p>
                <p className="text-xs text-base-content/60 mt-0.5">
                  {t('line_add_friend_subtitle', { account: account?.name ?? '' })}
                </p>
              </div>
              <a
                href={`https://line.me/R/ti/p/${encodeURIComponent(lineOaBasicId!)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-xs whitespace-nowrap flex-shrink-0"
              >
                {t('line_add_friend_cta')}
              </a>
            </div>
          </div>
        )}

        {/* Announcements section */}
        {announcements !== undefined && (
          <AnnouncementsSection announcements={announcements} t={t} />
        )}

        {/* Lease card — tappable when the lease has a document, since that is
            what a tenant reaches for first (owner report 2026-08-20: "I suspect
            the green banner to be click and show lease pdf"). Rendered as a
            plain div when there is nothing to open, so it never looks
            interactive without being interactive. */}
        <div
          {...(leasePdfAvailable
            ? {
                role: 'button' as const,
                tabIndex: 0,
                onClick: openLeasePdf,
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openLeasePdf()
                  }
                },
                'aria-label': t('view_lease_agreement'),
              }
            : {})}
          className={`bg-primary rounded-xl p-4 text-primary-content shadow-md ${
            leasePdfAvailable ? 'cursor-pointer active:opacity-90 transition-opacity' : ''
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold opacity-80">{t('lease_card_title')}</h2>
            {leasePdfAvailable && <DocumentTextIcon className="w-5 h-5 opacity-80" />}
          </div>
          {customer?.active_lease ? (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="opacity-70">{t('property')}</span>
                <span className="font-medium">{customer.active_lease.property_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-70">{t('unit')}</span>
                <span className="font-medium">{customer.active_lease.unit_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-70">{t('period')}</span>
                <span className="font-medium">
                  {formatDate(customer.active_lease.started_at)}
                  {customer.active_lease.ended_at && ` – ${formatDate(customer.active_lease.ended_at)}`}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm opacity-70">{t('no_active_lease')}</p>
          )}
        </div>

        {/* Shown when the lease references a document that is no longer in
            storage — the tenant cannot rebuild one, so tell them what to ask
            for instead of leaving a dead entry. */}
        {leasePdfKey && leasePdfMissing && (
          <div className="flat-card p-4">
            <div className="flex items-start gap-3">
              <div className="icon-container">
                <DocumentTextIcon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-base-content">{t('lease_pdf_unavailable_title')}</p>
                <p className="text-xs opacity-70 mt-1">{t('lease_pdf_unavailable_body')}</p>
              </div>
            </div>
          </div>
        )}

        {leasePdfError && (
          <p className="text-error text-sm px-1" role="alert">{leasePdfError}</p>
        )}

        {/* EP-135 Tier 1a — Meeting card. Shown only when the person holds at
            least one active OwnerContract (pure shared owner OR dual-role).
            The meeting is a building-level event — a tenant who does NOT own
            a unit in the building is excluded (their RentalLease would carry
            a property_id that doesn't match the condominium property, which
            Tier 2/3 will filter on; for now we show the entry point and let
            the meetings list page do the scoping). */}
        {hasGovernanceRole && (
          <Link to="/meetings" className="flat-card list-item-row block">
            <div className="flex items-center gap-3">
              <div className="icon-container">
                <CalendarDaysIcon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-base-content">
                  {t('meetings_card_title')}
                </p>
                <p className="text-xs text-base-content/60">
                  {governanceContracts.length === 1
                    ? t('meetings_card_subtitle_one', { count: 1 })
                    : t('meetings_card_subtitle_other', {
                        count: governanceContracts.length,
                      })}
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-base-content/40 flex-shrink-0" />
            </div>
          </Link>
        )}

        {/* Quick stats */}
        <div className="grid grid-cols-2 gap-3">
          {/* Unpaid invoices */}
          <div className="flat-card p-4 flex flex-col gap-2">
            <div className="icon-container">
              <DocumentTextIcon className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold text-warning leading-none">{unpaidCount}</p>
            <p className="text-xs text-base-content/60">{t('unpaid_invoices')}</p>
            {unpaidCount > 0 && (
              <Link to="/invoices" className="btn btn-warning btn-xs mt-1">
                {t('pay_now')}
              </Link>
            )}
          </div>

          {/* Pending parcels */}
          <div className="flat-card p-4 flex flex-col gap-2">
            <div className="icon-container">
              <InboxStackIcon className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold text-info leading-none">{pendingParcelCount}</p>
            <p className="text-xs text-base-content/60">{t('pending_parcels')}</p>
            <Link to="/parcels" className="btn btn-ghost btn-xs mt-1">
              {t('view_parcels')}
            </Link>
          </div>
        </div>

        {/* My Vehicles */}
        <Link to="/vehicles" className="flat-card list-item-row block">
          <div className="flex items-center gap-3">
            <div className="icon-container">
              <TruckIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-base-content">{t('my_vehicles')}</p>
              <p className="text-xs text-base-content/60">{t('vehicles_count', { count: vehicleCount })}</p>
            </div>
            <ChevronRightIcon className="w-4 h-4 text-base-content/40 flex-shrink-0" />
          </div>
        </Link>

        {/* WIN Pickup (dual-gate: win_pickup flag AND account.win_pickup_enabled) */}
        {showWinPickup && (
          <Link to="/win-pickup" className="flat-card list-item-row block">
            <div className="flex items-center gap-3">
              <div className="icon-container">
                <MapPinIcon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-base-content">{t('win_pickup')}</p>
                <p className="text-xs text-base-content/60">{t('win_pickup_subtitle')}</p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-base-content/40 flex-shrink-0" />
            </div>
          </Link>
        )}

        {/* Rewards (shown only when loyaltyPoints flag is on) */}
        {loyaltyPoints && (
          <Link to="/rewards" className="flat-card list-item-row block">
            <div className="flex items-center gap-3">
              <div className="icon-container">
                <SparklesIcon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-base-content">{t('my_rewards')}</p>
                <p className="text-xs text-base-content/60">
                  {t('rewards_points', { count: rewardsBalance?.points_balance ?? 0 })}
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-base-content/40 flex-shrink-0" />
            </div>
          </Link>
        )}

        {/* EP-160 T160-104l (S6): the tenant's PDPA requests. */}
        <Link to="/my-data" className="flat-card list-item-row block">
          <div className="flex items-center gap-3">
            <div className="icon-container">
              <ShieldCheckIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-base-content">{tMyData('home_row_title')}</p>
              <p className="text-xs text-base-content/60">{tMyData('home_row_subtitle')}</p>
            </div>
            <ChevronRightIcon className="w-4 h-4 text-base-content/40 flex-shrink-0" />
          </div>
        </Link>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Announcements section
// ---------------------------------------------------------------------------

interface AnnouncementsSectionProps {
  announcements: Announcement[]
  t: TFunction
}

function AnnouncementsSection({ announcements, t }: AnnouncementsSectionProps) {
  return (
    <div className="space-y-2">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <MegaphoneIcon className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-semibold text-base-content">{t('announcements_title')}</h2>
      </div>

      {announcements.length === 0 ? (
        <p className="text-xs text-base-content/50 py-2">{t('no_announcements')}</p>
      ) : (
        <div className="space-y-2">
          {announcements.map((announcement) => (
            <AnnouncementCard key={announcement.id} announcement={announcement} t={t} />
          ))}
        </div>
      )}
    </div>
  )
}

interface AnnouncementCardProps {
  announcement: Announcement
  t: TFunction
}

function AnnouncementCard({ announcement, t }: AnnouncementCardProps) {
  const { formatDate } = useDateFormatter()
  const preview =
    announcement.body.length > 100
      ? `${announcement.body.slice(0, 100).trimEnd()}…`
      : announcement.body

  const publishedDate = formatDate(announcement.published_at)

  return (
    <div className="flat-card p-4 space-y-2">
      <p className="text-sm font-semibold text-base-content leading-snug">{announcement.title}</p>
      <p className="text-xs text-base-content/60">{publishedDate}</p>
      <p className="text-xs text-base-content/80 leading-relaxed">{preview}</p>
      <Link
        to={`/announcements/${announcement.id}`}
        className="text-xs text-primary font-semibold"
      >
        {t('read_more')}
      </Link>
    </div>
  )
}
